/**
 * Piecewise-linear gain envelopes, in seconds. Values before the first / after the last point are
 * held. An envelope with a single point is constant.
 */
export interface Envelope {
  times: number[];
  values: number[];
}

export function constantEnvelope(value: number): Envelope {
  return { times: [0], values: [value] };
}

export function evaluateEnvelope(env: Envelope, t: number): number {
  const { times, values } = env;
  const n = times.length;
  if (n === 0) return 1;
  if (t <= times[0]!) return values[0]!;
  if (t >= times[n - 1]!) return values[n - 1]!;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid]! <= t) lo = mid;
    else hi = mid;
  }
  const t0 = times[lo]!;
  const t1 = times[hi]!;
  const span = t1 - t0;
  if (span <= 0) return values[hi]!;
  return values[lo]! + ((values[hi]! - values[lo]!) * (t - t0)) / span;
}

export function isConstant(env: Envelope): boolean {
  return env.values.every((v) => v === env.values[0]);
}

/**
 * Multiplies two envelopes. Breakpoints of both are merged; between breakpoints the product of two
 * linear ramps is quadratic, so long spans are subdivided to keep the error inaudible.
 */
export function multiplyEnvelopes(a: Envelope, b: Envelope): Envelope {
  if (isConstant(b)) return scaleEnvelope(a, b.values[0] ?? 1);
  if (isConstant(a)) return scaleEnvelope(b, a.values[0] ?? 1);
  const merged = [...new Set([...a.times, ...b.times])].sort((x, y) => x - y);
  const times: number[] = [];
  for (let i = 0; i < merged.length; i++) {
    const t = merged[i]!;
    times.push(t);
    const next = merged[i + 1];
    if (next !== undefined) {
      const steps = 4;
      for (let s = 1; s < steps; s++) times.push(t + ((next - t) * s) / steps);
    }
  }
  return { times, values: times.map((t) => evaluateEnvelope(a, t) * evaluateEnvelope(b, t)) };
}

export function scaleEnvelope(env: Envelope, factor: number): Envelope {
  return { times: [...env.times], values: env.values.map((v) => v * factor) };
}

export function shiftEnvelope(env: Envelope, dt: number): Envelope {
  return { times: env.times.map((t) => t + dt), values: [...env.values] };
}

/** Restricts an envelope to `[t0, t1]`, inserting exact boundary values. */
export function cropEnvelope(env: Envelope, t0: number, t1: number): Envelope {
  const times = [t0];
  const values = [evaluateEnvelope(env, t0)];
  env.times.forEach((t, i) => {
    if (t > t0 && t < t1) {
      times.push(t);
      values.push(env.values[i]!);
    }
  });
  if (t1 > t0) {
    times.push(t1);
    values.push(evaluateEnvelope(env, t1));
  }
  return { times, values };
}

/** Linear ramp from `v0` at `t0` to `v1` at `t1`. */
export function rampEnvelope(t0: number, v0: number, t1: number, v1: number): Envelope {
  return { times: [t0, t1], values: [v0, v1] };
}
